import {
  Button,
  Column,
  Copy,
  Row,
  Square,
} from '@grubhubprod/gh-cookbook-react';
import React, { useState } from 'react';
import { GroupFormFields } from 'src/components/oneCorp/features/MealCredit/components/Groups/GroupForm/GroupForm.utils';
import Table from 'src/components/Table/Table';
import { NoResultsMsg } from 'src/components/Table/NoResultsMsg';
import { LocTagInvite } from 'src/types/LineOfCredit';
import { GroupForm } from 'src/components/oneCorp/features/MealCredit/components/Groups/GroupForm/GroupForm';
import { cleanAmount } from 'src/components/oneCorp/features/MealCredit/hooks/assignAllowanceAmount.utils';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { GroupsTableItem } from './GroupsTableItem';

const MAX_BODY_HEIGHT = '225px';

export interface GroupsTableProps {
  tagInvites: LocTagInvite[];
  filteredTagInvites: LocTagInvite[];
  initialTagInvitesLength: number;
  removeGroup: (groupToRemove: LocTagInvite) => Promise<void>;
  replaceGroup: (
    newGroup: LocTagInvite,
    originalGroupIdEdited: string
  ) => Promise<void>;
  addGroup: (newGroup: LocTagInvite) => Promise<void>;
  isAdding: boolean;
  isRemoving: boolean;
  isUpdating: boolean;
  hasNoResultsWithQuery: boolean;
  hasNoResultsWithoutQuery: boolean;
}

export default function GroupsTable({
  tagInvites,
  filteredTagInvites,
  initialTagInvitesLength,
  removeGroup,
  replaceGroup,
  addGroup,
  isAdding,
  isRemoving,
  isUpdating,
  hasNoResultsWithQuery,
  hasNoResultsWithoutQuery,
}: GroupsTableProps): JSX.Element {
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [currentGroupIdRemoved, setCurrentGroupIdRemoved] = useState(null);
  const [currentGroupIdUpdated, setCurrentGroupIdUpdated] = useState(null);

  const { isDesktop } = useScreenSizes();

  const onRemoveGroupHandle = async (group: LocTagInvite) => {
    setCurrentGroupIdRemoved(group.id);
    setCurrentGroupIdUpdated(null);
    await removeGroup(group);
  };

  return (
    <Table
      bodyMaxHeight={isDesktop ? MAX_BODY_HEIGHT : 'auto'}
      headerInsets="sm"
      header={() => (
        <Row>
          <Column phone={4}>
            <Copy variant="body" color="secondaryText">
              Group name
            </Copy>
          </Column>
          <Column phone={4}>
            <Copy variant="body" color="secondaryText">
              Credit amount/employee
            </Copy>
          </Column>
        </Row>
      )}
      body={() => (
        <>
          {filteredTagInvites.map((group, index) => {
            return (
              <Square
                inset="sm"
                key={group.id}
                borderBottom={
                  index === initialTagInvitesLength - 1 ? 'none' : '1px solid'
                }
                borderColor="lightLine"
              >
                {currentGroupIdUpdated === group.id ? (
                  <GroupForm
                    tagInvites={tagInvites}
                    initGroup={group}
                    onHandleForm={async (newGroup) => {
                      await replaceGroup(
                        { ...newGroup, budget: cleanAmount(newGroup.budget) },
                        group.id
                      );
                      setCurrentGroupIdUpdated(null);
                    }}
                    onCancel={() => setCurrentGroupIdUpdated(null)}
                  />
                ) : (
                  <GroupsTableItem
                    group={group}
                    onRemoveGroup={() => onRemoveGroupHandle(group)}
                    onOpenUpdateGroupForm={() => {
                      setShowAddGroupForm(false);
                      setCurrentGroupIdUpdated(group.id);
                    }}
                    isUpdating={isUpdating || isAdding}
                    isRemoving={
                      isRemoving && group.id === currentGroupIdRemoved
                    }
                    isDisabled={isRemoving || isUpdating}
                  />
                )}
              </Square>
            );
          })}

          {hasNoResultsWithoutQuery && (
            <NoResultsMsg message="You haven’t selected a group and set a credit amount yet." />
          )}
          {hasNoResultsWithQuery && (
            <NoResultsMsg message="We couldn’t find what you were looking for. Give it another go." />
          )}
        </>
      )}
      showFooter
      footer={() => (
        <Square inset="sm" width="100%">
          {showAddGroupForm ? (
            <GroupForm
              tagInvites={tagInvites}
              onHandleForm={async (group: GroupFormFields) => {
                await addGroup({
                  ...group,
                  budget: cleanAmount(group.budget),
                });
                setShowAddGroupForm(false);
              }}
              isAdding={isAdding}
              onCancel={() => setShowAddGroupForm(false)}
            />
          ) : (
            <Button
              variant="primary"
              disabled={isRemoving || isUpdating || isAdding}
              onClick={() => setShowAddGroupForm(true)}
            >
              Assign group
            </Button>
          )}
        </Square>
      )}
    />
  );
}
